import { compactTypography, emrTypography, responsiveTypography } from './responsive';

export const UI_DENSITY = {
  COMFORTABLE: 'comfortable',
  COMPACT: 'compact',
  EMR: 'emr',
};

export const UI_DENSITY_ORDER = [UI_DENSITY.COMFORTABLE, UI_DENSITY.COMPACT, UI_DENSITY.EMR];

export function normalizeDensity(value) {
  if (!value) return UI_DENSITY.COMFORTABLE;
  const normalized = String(value).toLowerCase();
  if (UI_DENSITY_ORDER.includes(normalized)) return normalized;
  return UI_DENSITY.COMFORTABLE;
}

export const DENSITY_PRESETS = {
  [UI_DENSITY.COMFORTABLE]: {
    id: UI_DENSITY.COMFORTABLE,
    label: 'Comfortable',
    description: 'More spacious; best for touch-heavy use.',
    spacing: 8,
    defaultComponentSize: 'medium',
    typographyScale: {
      ...responsiveTypography,
      subtitle1: {
        fontSize: { xs: '0.9375rem', sm: '1rem', md: '1rem' },
        lineHeight: 1.5,
        fontWeight: 500,
      },
      subtitle2: {
        fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.875rem' },
        lineHeight: 1.5,
        fontWeight: 500,
      },
      button: {
        fontSize: { xs: '0.875rem', sm: '0.875rem', md: '0.9375rem' },
        textTransform: 'none',
        fontWeight: 600,
      },
      overline: {
        fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.75rem' },
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      },
    },
    components: {
      button: {
        defaultSize: 'medium',
        sizeSmall: { minHeight: 32, padding: '6px 12px' },
        sizeMedium: { minHeight: 40, padding: '8px 16px' },
        sizeLarge: { minHeight: 44, padding: '10px 20px' },
      },
      iconButton: {
        defaultSize: 'medium',
        sizeSmall: { width: 32, height: 32 },
        sizeMedium: { width: 40, height: 40 },
        sizeLarge: { width: 48, height: 48 },
      },
      chip: {
        defaultSize: 'medium',
        sizeSmall: { height: 24, fontSize: '0.75rem' },
        sizeMedium: { height: 28, fontSize: '0.8125rem' },
      },
      tabs: {
        tabsMinHeight: 44,
        tabMinHeight: 36,
        tabPadding: '6px 12px',
      },
      tableCell: {
        padding: '10px 16px',
        fontSize: '0.875rem',
        headFontSize: '0.75rem',
      },
      cardContent: {
        padding: 16,
      },
      dialog: {
        titlePadding: '16px 20px',
        titleFontSize: '1.1rem',
        contentPadding: '12px 20px',
      },
      input: {
        defaultSize: 'medium',
        inputPadding: '10px 12px',
        inputPaddingSmall: '8px 10px',
      },
      dataGrid: {
        density: 'standard',
        rowHeight: 44,
        columnHeaderHeight: 44,
      },
    },
  },
  [UI_DENSITY.COMPACT]: {
    id: UI_DENSITY.COMPACT,
    label: 'Compact',
    description: 'Enterprise default; dense but readable for desktop.',
    spacing: 6,
    defaultComponentSize: 'small',
    typographyScale: compactTypography,
    components: {
      button: {
        defaultSize: 'small',
        sizeSmall: { minHeight: 28, padding: '4px 10px' },
        sizeMedium: { minHeight: 32, padding: '6px 14px' },
        sizeLarge: { minHeight: 36, padding: '8px 18px' },
      },
      iconButton: {
        defaultSize: 'small',
        sizeSmall: { width: 28, height: 28 },
        sizeMedium: { width: 36, height: 36 },
        sizeLarge: { width: 44, height: 44 },
      },
      chip: {
        defaultSize: 'small',
        sizeSmall: { height: 22, fontSize: '0.6875rem' },
        sizeMedium: { height: 24, fontSize: '0.75rem' },
      },
      tabs: {
        tabsMinHeight: 40,
        tabMinHeight: 32,
        tabPadding: '6px 10px',
      },
      tableCell: {
        padding: '6px 12px',
        fontSize: '0.8125rem',
        headFontSize: '0.6875rem',
      },
      cardContent: {
        padding: 12,
      },
      dialog: {
        titlePadding: '12px 16px',
        titleFontSize: '1rem',
        contentPadding: '10px 16px',
      },
      input: {
        defaultSize: 'small',
        inputPadding: '8px 10px',
        inputPaddingSmall: '6px 8px',
      },
      dataGrid: {
        density: 'compact',
        rowHeight: 40,
        columnHeaderHeight: 40,
      },
    },
  },
  [UI_DENSITY.EMR]: {
    id: UI_DENSITY.EMR,
    label: 'EMR',
    description: 'Maximum density for data-heavy clinical screens.',
    spacing: 4,
    defaultComponentSize: 'small',
    typographyScale: emrTypography,
    components: {
      button: {
        defaultSize: 'small',
        sizeSmall: { minHeight: 24, padding: '3px 8px' },
        sizeMedium: { minHeight: 28, padding: '4px 10px' },
        sizeLarge: { minHeight: 32, padding: '6px 12px' },
      },
      iconButton: {
        defaultSize: 'small',
        sizeSmall: { width: 26, height: 26 },
        sizeMedium: { width: 32, height: 32 },
        sizeLarge: { width: 40, height: 40 },
      },
      chip: {
        defaultSize: 'small',
        sizeSmall: { height: 20, fontSize: '0.6875rem' },
        sizeMedium: { height: 22, fontSize: '0.75rem' },
      },
      tabs: {
        tabsMinHeight: 36,
        tabMinHeight: 28,
        tabPadding: '4px 8px',
      },
      tableCell: {
        padding: '4px 8px',
        fontSize: '0.75rem',
        headFontSize: '0.6875rem',
      },
      cardContent: {
        padding: 10,
      },
      dialog: {
        titlePadding: '10px 14px',
        titleFontSize: '0.9375rem',
        contentPadding: '8px 14px',
      },
      input: {
        defaultSize: 'small',
        inputPadding: '6px 8px',
        inputPaddingSmall: '5px 7px',
      },
      dataGrid: {
        density: 'compact',
        rowHeight: 36,
        columnHeaderHeight: 36,
      },
    },
  },
};

export const MOBILE_DENSITY_MULTIPLIERS = {
  padding: 0.75,
  borderRadius: 0.8,
  margin: 0.7,
  spacing: 0.75,
};

export const MOBILE_BREAKPOINT = '@media (max-width: 600px)';

export function getMobileComponentValues(baseComponents) {
  const { padding: pMult, borderRadius: brMult, margin: mMult } = MOBILE_DENSITY_MULTIPLIERS;

  const parsePadding = (paddingStr) => {
    if (typeof paddingStr === 'number') return Math.round(paddingStr * pMult);
    const parts = paddingStr.split(' ').map((p) => {
      const val = parseFloat(p);
      return `${Math.round(val * pMult)}px`;
    });
    return parts.join(' ');
  };

  return {
    cardContent: {
      padding: Math.round(baseComponents.cardContent.padding * pMult),
    },
    dialog: {
      titlePadding: parsePadding(baseComponents.dialog.titlePadding),
      contentPadding: parsePadding(baseComponents.dialog.contentPadding),
      borderRadius: Math.round(16 * brMult),
    },
    tableCell: {
      padding: parsePadding(baseComponents.tableCell.padding),
    },
    card: {
      borderRadius: Math.round(16 * brMult),
    },
    paper: {
      borderRadius: Math.round(12 * brMult),
    },
    alert: {
      paddingMultiplier: pMult,
    },
    listItemButton: {
      margin: `${Math.round(2 * mMult)}px ${Math.round(8 * mMult)}px`,
    },
    tabs: {
      tabMinHeight: Math.round(baseComponents.tabs.tabMinHeight * 0.9),
      tabPadding: parsePadding(baseComponents.tabs.tabPadding),
    },
  };
}

export function getDensityPreset(density) {
  const preset = DENSITY_PRESETS[normalizeDensity(density)];
  return {
    ...preset,
    mobile: getMobileComponentValues(preset.components),
  };
}

